import { FC, MouseEvent, ReactNode, useCallback, useRef, useState } from 'react';

import { ZOOM_ENABLING_DELAY } from './constants';
import { ZoomContext, ZoomContextType } from './ZoomContext';

export const ZoomProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [coordinates, setCoords] = useState<{ x: number | null; y: number | null }>({ x: null, y: null });
    const [imageToZoomSrc, setImageToZoomSrc] = useState<string | null>(null);
    const [imageToZoomDimensions, setImageToZoomDimensions] = useState({ height: 1, width: 1 });
    const [isCursorHovering, setIsCursorHovering] = useState(false);

    const updateCoordinates = useCallback(
        (event: MouseEvent<HTMLImageElement>) => {
            const boundaries = (event.target as HTMLImageElement).getBoundingClientRect();
            setImageToZoomDimensions(boundaries);
            const x = Math.max(Math.round(event.clientX - (boundaries.left ?? 0)), 0);
            const y = Math.max(Math.round(event.clientY - (boundaries.top ?? 0)), 0);

            setCoords({ x, y });
        },
        [setCoords],
    );

    const delayedEnablingTimerId = useRef<number>(null);

    const enableHoveringState = useCallback(() => {
        window.clearTimeout(delayedEnablingTimerId.current || undefined);
        delayedEnablingTimerId.current = window.setTimeout(() => setIsCursorHovering(true), ZOOM_ENABLING_DELAY);
    }, []);

    const disableHoveringState = useCallback(() => {
        window.clearTimeout(delayedEnablingTimerId.current || undefined);
        setIsCursorHovering(false);
    }, []);

    const contextValue: ZoomContextType = {
        ...coordinates,
        imageToZoomSrc,
        imageToZoomDimensions,
        isCursorHovering,
        setImageToZoomSrc,
        updateCoordinates,
        enableHoveringState,
        disableHoveringState,
    };

    return <ZoomContext.Provider value={contextValue}>{children}</ZoomContext.Provider>;
};
